import type { Account } from '@/api/generated/model';
import {
  PillarMetricSubtitleRow,
  PillarMetricWithInfo,
} from '@/components/ui/pillar-metric-with-info';
import { computeCheckingBalance } from '@/features/accounts/checkingBalance';
import { useLanguage } from '@/i18n/language-hooks';
import { formatCurrency, useDefaultCurrency } from '@/lib/utils';
import { useMemo } from 'react';

interface OverviewPillarHeroInput {
  netWorth: number;
  accounts: Account[];
  isLoading: boolean;
}

export function useOverviewPillarHero({ netWorth, accounts, isLoading }: OverviewPillarHeroInput) {
  const { t } = useLanguage();
  const currency = useDefaultCurrency();
  const checkingBalance = useMemo(() => computeCheckingBalance(accounts), [accounts]);

  return useMemo(
    () => ({
      title: (
        <PillarMetricWithInfo
          value={formatCurrency(netWorth, currency)}
          numericValue={netWorth}
          formatValue={(n) => formatCurrency(n, currency)}
          info={t('overview.hero.netWorthInfo')}
          infoAriaLabel={t('overview.hero.netWorthInfoAria')}
          isLoading={isLoading}
        />
      ),
      subtitle: (
        <PillarMetricSubtitleRow
          label={t('overview.hero.liquidAssets')}
          value={formatCurrency(checkingBalance, currency)}
          numericValue={checkingBalance}
          formatValue={(n) => formatCurrency(n, currency)}
          info={t('banking.hero.checkingBalanceInfo')}
          infoAriaLabel={t('banking.hero.checkingBalanceInfoAria')}
          isLoading={isLoading}
        />
      ),
    }),
    [checkingBalance, currency, isLoading, netWorth, t]
  );
}
